import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { ITBox, QboAlert, QboFormGroup, QboTextField } from '@ui/Components';
import { useTranslation } from 'react-i18next';
import useEmailFromCSV from '@hooks/useEmailFromCSV';
import { FormControl, Grid } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import OrganizationFilterSection from '../Shared/OrganizationFilterSection';
import OrganizationAutocompleteSection from '../Shared/OrganizationAutocompleteSection';

export default function MailboxDeactivationFormSection({
  errorMessage,
  isValidating,
  onValidate,
  form,
}) {
  const [organization, setOrganization] = useState(null);
  const { t } = useTranslation();
  const [emailLists, setEmailLists] = useState([]);
  const { handleOnFileUpload } = useEmailFromCSV();
  const inputRef = useRef(null);

  useEffect(() => {
    if (emailLists.length > 0) {
      form.change('emailAccounts', emailLists.toString());
    }
  }, [emailLists]);

  const handleSubmit = (e) => {
    e.preventDefault();
    form.validate();
  };

  useEffect(() => {
    if (form.isValid) {
      onValidate(form.values);
      form.reset(true);
    }
  }, [form.isValid]);

  return (
    <ITBox className="TransferSubscriptionFormPage__wrapper" direction="column">
      {errorMessage && (
        <QboAlert type="error" style={{ fontSize: '1.15em' }}>
          {errorMessage}
        </QboAlert>
      )}
      <QboFormGroup
        onSubmit={handleSubmit}
        withSubmitButton
        buttonLabel="Validate"
        buttonDisabled={isValidating}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <QboTextField
              id="supportTicket"
              required
              disabled={isValidating}
              placeholder="Support Ticket"
              label="Support Ticket"
              fullWidth
              value={form.values.supportTicket}
              onChange={(e) => {
                form.change('supportTicket', e.currentTarget.value);
              }}
              errorMessage={form.errors.supportTicket}
              inputProps={{ maxLength: 225 }}
            />
          </Grid>
          <Grid item xs={6}>
            <QboTextField
              id="escalationTicket"
              disabled={isValidating}
              placeholder="Escalation Ticket"
              label="Escalation Ticket"
              fullWidth
              value={form.values.escalationTicket}
              onChange={(e) => {
                form.change('escalationTicket', e.currentTarget.value);
              }}
              inputProps={{ maxLength: 225 }}
            />
          </Grid>
        </Grid>
        <QboTextField
          id="description"
          disabled={isValidating}
          placeholder="Description"
          label="Description"
          fullWidth
          value={form.values.description}
          onChange={(e) => {
            form.change('description', e.currentTarget.value);
          }}
          inputProps={{ maxLength: 225 }}
        />

        <FormControl className="FormControl__default-wrapper FormControl__full-width-wrapper">
          <div className="QboTextField__label" id="transferType">
            Organization
            <span style={{ color: 'red' }}> *</span>
          </div>
          <OrganizationAutocompleteSection
            value={form.values.organization}
            onChange={(newValue) => form.change('organization', newValue)}
            disabled={isValidating}
            hasError={!!form.errors.organization}
          />
          {form.errors.organization && (
            <span className="QboTextField__error-text">
              <ErrorIcon />
              {` ${form.errors.organization}`}
            </span>
          )}
        </FormControl>

        <QboTextField
          id="emailAccounts"
          required
          disabled={isValidating}
          placeholder={t('bulk_purge.labels.email_accounts')}
          label={t('bulk_purge.labels.email_accounts')}
          multiline
          rows={5}
          fullWidth
          value={form.values.emailAccounts}
          onChange={(e) => {
            form.change('emailAccounts', e.currentTarget.value);
          }}
          errorMessage={form.errors.emailAccounts}
        />
        <QboTextField
          id="csvFile"
          disabled={isValidating}
          placeholder={t('bulk_purge.labels.csv_file')}
          label={t('bulk_purge.labels.csv_file')}
          type="file"
          inputRef={inputRef}
          fullWidth
          inputProps={{ maxLength: 225 }}
          onChange={(event) => {
            handleOnFileUpload(event, setEmailLists);
          }}
        />
      </QboFormGroup>
    </ITBox>
  );
}

MailboxDeactivationFormSection.propTypes = {
  errorMessage: PropTypes.string,
  isValidating: PropTypes.bool,
  onValidate: PropTypes.func,
  form: PropTypes.object,
};

MailboxDeactivationFormSection.defaultProps = {
  errorMessage: null,
  isValidating: false,
  onValidate: () => {},
  form: {},
};
